import { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth-store';
import {
    checkAccountStatus,
    getUserType,
    AccountStatus,
    UserType,
    type AccountStatusCheck,
} from '../lib/auth/account-status';
import toast from 'react-hot-toast';

interface AccountStatusGuardProps {
    children: React.ReactNode;
    requireEmailVerified?: boolean;
    requireProfileCompleted?: boolean;
    requirePartnerVerified?: boolean;
    bypassForPaths?: string[]; // Paths that bypass status checks (e.g., /partner/complete-profile)
}

/**
 * AccountStatusGuard - Layer 2 Security Component
 * 
 * Checks account status requirements before rendering children:
 * - Email verification
 * - Profile completion
 * - Partner verification
 * - Account suspension/ban
 * 
 * Redirects to appropriate pages if requirements not met.
 */
export function AccountStatusGuard({
    children,
    requireEmailVerified = true,
    requireProfileCompleted = false,
    requirePartnerVerified = false,
    bypassForPaths = [],
}: AccountStatusGuardProps) {
    const { t } = useTranslation();
    const { user, profileLoaded } = useAuthStore();
    const [statusCheck, setStatusCheck] = useState<AccountStatusCheck | null>(null);
    const [isChecking, setIsChecking] = useState(true);
    const [userType, setUserType] = useState<UserType | null>(null);
    const hasShownToast = useRef(false);

    // Check if current path should bypass status checks
    const currentPath = window.location.pathname;
    const shouldBypass = bypassForPaths.some(path => currentPath.startsWith(path));

    useEffect(() => {
        async function performStatusCheck() {
            if (!user || !profileLoaded) {
                setIsChecking(false);
                return;
            }

            // Skip check if on bypass path
            if (shouldBypass) {
                setStatusCheck({ status: AccountStatus.ACTIVE, isAllowed: true });
                setIsChecking(false);
                return;
            }

            try {
                // Get user type
                const type = await getUserType(user.id);
                setUserType(type);

                if (!type) {
                    setIsChecking(false);
                    return;
                }

                // Check account status
                const check = await checkAccountStatus(user.id, type);
                setStatusCheck(check);

                // Apply requirement filters
                let finalCheck = check;

                // If email verification is required but not met
                if (requireEmailVerified && check.status === AccountStatus.PENDING_EMAIL_VERIFICATION) {
                    finalCheck = check; // Block access
                }

                // If profile completion is required but not met
                if (requireProfileCompleted && check.status === AccountStatus.PROFILE_INCOMPLETE) {
                    finalCheck = check; // Block access
                }

                // If partner verification is required but not met
                if (requirePartnerVerified && check.status === AccountStatus.PENDING_PARTNER_VERIFICATION) {
                    finalCheck = check; // Block access
                }

                setStatusCheck(finalCheck);

                // Show toast notification if access is denied
                if (finalCheck && !finalCheck.isAllowed && finalCheck.redirectTo && !hasShownToast.current) {
                    if (finalCheck.message) {
                        toast.error(finalCheck.message, { duration: 5000 });
                        hasShownToast.current = true;
                    }

                    // Log for security monitoring

                }
            } catch (error) {

                // Fail open to prevent blocking legitimate users
                setStatusCheck({ status: AccountStatus.ACTIVE, isAllowed: true });
            } finally {
                setIsChecking(false);
            }
        }

        performStatusCheck();
    }, [user, profileLoaded, requireEmailVerified, requireProfileCompleted, requirePartnerVerified, shouldBypass]);

    // Show loading state while checking
    if (isChecking || !profileLoaded) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center space-y-4">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                    <p className="text-sm text-muted-foreground">{t('common.verifyingAccountStatus', 'Verifying account status...')}</p>
                </div>
            </div>
        );
    }

    // If status check failed, redirect
    if (statusCheck && !statusCheck.isAllowed && statusCheck.redirectTo) {
        return <Navigate to={statusCheck.redirectTo} replace />;
    }

    // All checks passed - render children
    return <>{children}</>;
}
